import React, { useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock, PhoneCall } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["NEW", "TO_CONTACT", "CONTACTED", "QUALIFIED", "APPOINTMENT", "TEST_DRIVE", "QUOTATION", "ORDER", "WON", "LOST"];
const LABEL = { NEW: "Nuovo", TO_CONTACT: "Da contattare", CONTACTED: "Contattato", QUALIFIED: "Qualificato", APPOINTMENT: "Appuntamento", TEST_DRIVE: "Prova", QUOTATION: "Preventivo", ORDER: "Ordine", WON: "Vinto", LOST: "Perso" };
const COLOR = { NEW: "#64748b", TO_CONTACT: "#0891b2", CONTACTED: "#7c3aed", QUALIFIED: "#059669", APPOINTMENT: "#d97706", TEST_DRIVE: "#0d9488", QUOTATION: "#ca8a04", ORDER: "#2563eb", WON: "#16a34a", LOST: "#dc2626" };

export default function LeadsAdmin() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [callbackOnly, setCallbackOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [orderValue, setOrderValue] = useState("");

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["admin-leads", statusFilter],
    queryFn: () => api.get("/admin/leads", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => r.data),
  });

  const mut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/admin/leads/${id}`, body).then((r) => r.data),
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["admin-leads"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setSelected(data); toast.success("Lead aggiornato"); },
    onError: () => toast.error("Errore aggiornamento"),
  });

  const shown = callbackOnly
    ? leads.filter((l) => l.requested_action === "callback" && ["NEW", "TO_CONTACT"].includes(l.status))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    : leads;

  const open = (l) => { setSelected(l); setNotes(l.notes || ""); setOrderValue(l.order_value != null ? String(l.order_value) : ""); };
  const fmt = (d) => d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Lead / CRM</h1><p className="text-slate-500" data-testid="leads-count">{shown.length} lead</p></div>
        <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" onClick={() => setCallbackOnly(!callbackOnly)} data-testid="lead-callback-filter"
          className={`rounded-full gap-2 font-bold ${callbackOnly ? "bg-[#EA155B] text-white border-[#EA155B] hover:bg-[#EA155B]/90 hover:text-white" : "bg-white text-[#EA155B] border-[#EA155B]"}`}>
          <PhoneCall size={16} /> Da richiamare
        </Button>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52 bg-white" data-testid="lead-status-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
        </div>
      </div>
      {callbackOnly && <p className="-mt-3 mb-4 text-sm text-slate-500" data-testid="lead-callback-hint">Solo richieste "Richiamami" ancora da contattare (stato Nuovo o Da contattare), dalla più vecchia alla più recente.</p>}

      {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                <th className="px-4 py-3 font-semibold">Nome</th><th className="px-4 py-3 font-semibold">Contatto</th>
                <th className="px-4 py-3 font-semibold">Provincia</th><th className="px-4 py-3 font-semibold">Origine</th>
                <th className="px-4 py-3 font-semibold">Stato</th><th className="px-4 py-3 font-semibold">Data</th>
              </tr></thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{callbackOnly ? "Nessuno in attesa di una telefonata." : "Nessun lead ancora. I lead dai form del sito appariranno qui."}</td></tr>}
                {shown.map((l) => (
                  <tr key={l.id} onClick={() => open(l)} className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{l.first_name} {l.last_name}{l.test_drive_requested && <span className="ml-2 text-xs text-amber-700">🚗 prova</span>}{l.requested_action === "callback" && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#EA155B] text-white text-xs font-bold px-2 py-0.5 align-middle" data-testid={`lead-callback-badge-${l.id}`}><PhoneCall size={12} /> Richiamami</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{l.phone}</td>
                    <td className="px-4 py-3 text-slate-600">{l.province || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{l.source}</td>
                    <td className="px-4 py-3"><span className="text-xs font-bold text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: COLOR[l.status] }}>{LABEL[l.status]}</span></td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="text-xl">{selected.first_name} {selected.last_name}</DialogTitle><DialogDescription>Dettaglio lead, gestione stato pipeline e note interne.</DialogDescription></DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="flex items-center gap-2 text-slate-700"><Phone size={16} className="text-emerald-700" /> <a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></p>
                {selected.email && <p className="flex items-center gap-2 text-slate-700"><Mail size={16} className="text-emerald-700" /> {selected.email}</p>}
                <p className="flex items-center gap-2 text-slate-700"><MapPin size={16} className="text-emerald-700" /> {selected.city} {selected.province} {selected.postal_code}</p>
                <p className="flex items-center gap-2 text-slate-500"><Clock size={16} /> {fmt(selected.created_at)}</p>
                <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                  <span>Tipo: <b>{selected.lead_type || "-"}</b></span><span>Azione: <b>{selected.requested_action}</b></span>
                  <span>Origine: <b>{selected.source}</b></span><span>Lead score: <b>{selected.lead_score}</b></span>
                  <span className="col-span-2 truncate">Landing: <b>{selected.landing_page || "/"}</b></span>
                  {selected.utm_source && <span className="col-span-2">UTM: {selected.utm_source}/{selected.utm_campaign}</span>}
                </div>
                {selected.notes && <p className="text-slate-600 bg-amber-50 rounded-lg p-3">📝 {selected.notes}</p>}

                <div>
                  <label className="font-semibold text-slate-700 text-sm">Stato pipeline</label>
                  <Select value={selected.status} onValueChange={(v) => mut.mutate({ id: selected.id, body: { status: v } })}>
                    <SelectTrigger className="mt-1" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 text-sm">Valore ordine (€) — per report ricavi</label>
                  <div className="flex gap-2 mt-1">
                    <input type="number" value={orderValue} onChange={(e) => setOrderValue(e.target.value)} placeholder="es. 8900" className="flex-1 h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="lead-order-value" />
                    <Button className="h-10 font-semibold text-white rounded-full px-4" style={{ backgroundColor: "var(--sc-amber)" }} onClick={() => mut.mutate({ id: selected.id, body: { order_value: parseFloat(orderValue) || 0 } })} data-testid="lead-save-order">Salva</Button>
                  </div>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 text-sm">Note interne</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1" data-testid="lead-notes" />
                  <Button className="mt-2 h-10 font-semibold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} onClick={() => mut.mutate({ id: selected.id, body: { notes } })} data-testid="lead-save-notes">Salva note</Button>
                </div>
                {selected.history?.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="font-semibold text-slate-700 text-sm mb-2">Storico stati</p>
                    {selected.history.map((h, i) => <p key={i} className="text-xs text-slate-500">{fmt(h.at)} → <b>{LABEL[h.status] || h.status}</b></p>)}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
